<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import IntegracoesAPI from 'dashboard/api/integracoesDoEscritorio';
import {
  FONTE,
  CONFERIDO_EM,
  linhasDasTarifas,
  promocao,
} from './tarifasDoAsaas';

// CONECTAR O ASAAS, GUIADO (11/10/2026). Victor: "do jeito que a Tramitação
// Inteligente utiliza, só que melhor"; e, vendo a primeira versão: "dá para
// deixar muito melhor, mais interativo, mais informativo — do outro lado
// provavelmente vai ter uma pessoa leiga". Estudo: docs/integracoes/ESTUDO-ASAAS-TRAMITACAO.md.
//
// O desenho: a capa da casa (`_advup-capa.scss`, a mesma do modal da OAB) com
// a TRILHA dos quatro passos — cada marco é botão, dá para voltar — e um passo
// por vez no corpo, cada um com a explicação de leigo e o gesto daquele passo.
// Quando a pessoa volta da aba do Asaas, a tela percebe e oferece ir direto
// para colar. "Conectar" faz tudo num gesto no servidor (confere a chave antes
// de gravar, liga, cadastra os avisos) e o resultado aparece item a item.
const props = defineProps({
  aberto: { type: Boolean, default: false },
});
const emit = defineEmits(['fechar', 'conectado']);

const { t } = useI18n();
const G = 'INTEGRACAO_ESCRITORIO.ASAAS.GUIA';
// Medido no painel do Asaas em 11/10/2026 (menu do perfil › Integrações ›
// Chaves de API). Se o endereço mudar, o passo escrito continua valendo.
const PAGINA_DE_CHAVES = 'https://www.asaas.com/customerApiAccessToken/index';
const SITE_DO_ASAAS = 'https://www.asaas.com/';
const FOTO = '/brand-assets/guias/asaas-chaves-de-api.webp';
const LOGO = '/brand-assets/logo-advup-white.webp';
const ARTE = '/brand-assets/briefing-fundo.webp';
const PASSOS = ['ABRIR', 'GERAR', 'COPIAR', 'COLAR'];

const passo = ref(0);
const feitos = ref(new Set());
const abriuAsaas = ref(false);
const voltouDoAsaas = ref(false);
const fotoAmpliada = ref(false);
const chave = ref('');
const mostrar = ref(false);
const conectando = ref(false);
const erro = ref('');
const resultado = ref(null);
const campo = ref(null);
const podeColar = typeof navigator !== 'undefined' && !!navigator.clipboard;

const tarifas = linhasDasTarifas();
const promo = promocao();

// O que a chave colada parece ser, antes de ir ao servidor: o prefixo diz o
// ambiente (docs.asaas.com/docs/chaves-de-api).
const leitura = computed(() => {
  const texto = chave.value.trim();
  if (!texto) return null;
  if (texto.startsWith('$aact_prod_'))
    return { tom: 'teal', chave: 'LEU_PRODUCAO' };
  if (texto.startsWith('$aact_hmlg_'))
    return { tom: 'ambar', chave: 'LEU_TESTE' };
  return { tom: 'rubi', chave: 'NAO_PARECE' };
});
// Mexeu na chave: o erro do servidor era da chave anterior.
watch(chave, () => {
  erro.value = '';
});

const podeConectar = computed(
  () => !!leitura.value && leitura.value.tom !== 'rubi' && !conectando.value
);

// T17: o passo atual aparece; os outros ocupam o lugar sem aparecer nem
// receber foco (inert), para a área não mudar de altura.
const estadoDoPasso = indice =>
  passo.value === indice
    ? { class: 'cas__passo--atual' }
    : { class: 'cas__passo--oculto', 'aria-hidden': 'true', inert: '' };

const irPara = indice => {
  if (passo.value < indice)
    feitos.value = new Set([...feitos.value, passo.value]);
  passo.value = indice;
  voltouDoAsaas.value = false;
  if (indice === 3) nextTick(() => campo.value?.focus());
};
const proximo = () => irPara(Math.min(passo.value + 1, PASSOS.length - 1));
const voltar = () => {
  passo.value = Math.max(passo.value - 1, 0);
};

// Voltou da aba do Asaas: oferece ir direto para colar (sem pular sozinho —
// a pessoa pode ter voltado só para reler o passo).
const aoVoltarParaATela = () => {
  if (abriuAsaas.value && passo.value < 3 && !resultado.value)
    voltouDoAsaas.value = true;
};

const abrirAsaas = () => {
  abriuAsaas.value = true;
  window.open(PAGINA_DE_CHAVES, '_blank', 'noopener,noreferrer');
  irPara(1);
};

watch(
  () => props.aberto,
  aberto => {
    if (aberto) {
      passo.value = 0;
      feitos.value = new Set();
      abriuAsaas.value = false;
      voltouDoAsaas.value = false;
      fotoAmpliada.value = false;
      chave.value = '';
      erro.value = '';
      resultado.value = null;
      window.addEventListener('focus', aoVoltarParaATela);
    } else {
      window.removeEventListener('focus', aoVoltarParaATela);
    }
  },
  { immediate: true }
);
onBeforeUnmount(() => window.removeEventListener('focus', aoVoltarParaATela));

const colar = async () => {
  try {
    chave.value = (await navigator.clipboard.readText()).trim();
    erro.value = '';
  } catch {
    // Sem permissão para ler a área de transferência: o campo aceita Ctrl+V.
    erro.value = t(`${G}.SEM_COLAR`);
    campo.value?.focus();
  }
};

const conectar = async () => {
  if (!podeConectar.value) return;
  conectando.value = true;
  erro.value = '';
  try {
    const { data } = await IntegracoesAPI.conectar('asaas', chave.value);
    resultado.value = data.resultado;
    feitos.value = new Set([0, 1, 2, 3]);
    chave.value = '';
    emit('conectado', data.payload);
  } catch (e) {
    erro.value = e?.response?.data?.resultado?.erro || t(`${G}.FALHOU`);
  } finally {
    conectando.value = false;
  }
};

// O que a conexão mediu, em frases — a linguagem do "Testar conexão".
const itens = computed(() => {
  const r = resultado.value;
  if (!r) return [];
  const lista = [
    {
      tom: 'teal',
      texto: t(r.ambiente === 'sandbox' ? `${G}.OK_TESTE` : `${G}.OK_PRODUCAO`),
    },
    r.avisos_ok
      ? { tom: 'teal', texto: t(`${G}.AVISOS_OK`) }
      : { tom: 'ambar', texto: t(`${G}.AVISOS_NAO`) },
  ];
  if (r.pode_sacar === false)
    lista.push({ tom: 'ambar', texto: t(`${G}.SACAR_NAO`) });
  return lista;
});

// Enquanto o servidor trabalha: o que está sendo feito, na ordem.
const etapas = computed(() => [
  t(`${G}.ETAPA_CONFERIR`),
  t(`${G}.ETAPA_LIGAR`),
  t(`${G}.ETAPA_AVISOS`),
]);
// Antes de conectar (T17): o que o "Conectar" faz, no espaço que o passo
// mais alto deixa livre — a dúvida natural de quem está colando a chave.
const oQueConectarFaz = computed(() => [
  { icone: 'i-ph-shield-check-fill', texto: t(`${G}.FAZ_CONFERIR`) },
  { icone: 'i-ph-plug-fill', texto: t(`${G}.FAZ_LIGAR`) },
  { icone: 'i-ph-bell-ringing-fill', texto: t(`${G}.FAZ_AVISOS`) },
]);
</script>

<template>
  <Teleport v-if="aberto" to="body">
    <div class="advup-modal-fundo" @click.self="emit('fechar')">
      <div
        v-modal-acessivel="() => emit('fechar')"
        class="advup-modal advup-modal--halo cas"
      >
        <header class="advup-capa">
          <img class="advup-capa__arte" :src="ARTE" alt="" aria-hidden="true" />
          <div class="advup-capa__conteudo">
            <img class="advup-capa__logo" :src="LOGO" alt="advup" />
            <span class="advup-capa__sup">{{ t(`${G}.SUP`) }}</span>
            <h2 class="advup-capa__tit">
              {{ resultado ? t(`${G}.PRONTO_TIT`) : t(`${G}.TITULO`) }}
            </h2>
            <p class="advup-capa__lead">
              {{ resultado ? t(`${G}.PRONTO_SUB`) : t(`${G}.LEAD`) }}
            </p>
            <ol class="advup-capa__trilha" :aria-label="t(`${G}.TRILHA_ARIA`)">
              <li v-for="(nome, i) in PASSOS" :key="nome">
                <span
                  v-if="i > 0"
                  class="advup-capa__fio"
                  :class="{ 'advup-capa__fio--feito': feitos.has(i - 1) }"
                  aria-hidden="true"
                />
                <button
                  type="button"
                  class="advup-capa__marco"
                  :class="{
                    'advup-capa__marco--atual': !resultado && passo === i,
                    'advup-capa__marco--feito': feitos.has(i),
                  }"
                  :aria-current="!resultado && passo === i ? 'step' : undefined"
                  :disabled="!!resultado"
                  @click="irPara(i)"
                >
                  <span class="advup-capa__ladrilho" aria-hidden="true">
                    <i v-if="feitos.has(i)" class="i-ph-check-bold" />
                    <template v-else>{{ i + 1 }}</template>
                  </span>
                  <span class="advup-capa__marco-rot">
                    {{ t(`${G}.${nome}_CURTO`) }}
                  </span>
                </button>
              </li>
            </ol>
          </div>
          <button
            type="button"
            class="advup-capa__fechar"
            :aria-label="t(`${G}.FECHAR`)"
            @click="emit('fechar')"
          >
            <i class="i-ph-x" aria-hidden="true" />
          </button>
        </header>

        <!-- ── Conectado ───────────────────────────────────────────────── -->
        <div v-if="resultado" class="advup-modal__corpo cas__corpo">
          <ul class="cas__lista" aria-live="polite">
            <li
              v-for="(item, i) in itens"
              :key="i"
              class="cas__linha"
              :class="`cas__linha--${item.tom}`"
            >
              <i
                :class="
                  item.tom === 'teal'
                    ? 'i-ph-check-circle-fill'
                    : 'i-ph-warning-circle-fill'
                "
                aria-hidden="true"
              />
              {{ item.texto }}
            </li>
          </ul>
          <section class="cas__caixa">
            <p class="cas__caixa-tit">{{ t(`${G}.AGORA_TIT`) }}</p>
            <ul class="cas__lista cas__lista--miuda">
              <li class="cas__linha">
                <i
                  class="i-ph-receipt-fill cas__ico-marca"
                  aria-hidden="true"
                />
                {{ t(`${G}.AGORA_1`) }}
              </li>
              <li class="cas__linha">
                <i
                  class="i-ph-check-square-offset-fill cas__ico-marca"
                  aria-hidden="true"
                />
                {{ t(`${G}.AGORA_2`) }}
              </li>
            </ul>
          </section>
        </div>

        <!-- ── Os passos, um por vez ───────────────────────────────────── -->
        <div v-else class="advup-modal__corpo cas__corpo" aria-live="polite">
          <!-- Voltou da aba do Asaas: atalho para colar. -->
          <div v-if="voltouDoAsaas" class="cas__voltou" role="status">
            <i class="i-ph-hand-waving-fill" aria-hidden="true" />
            <span>{{ t(`${G}.VOLTOU`) }}</span>
            <button
              type="button"
              class="advup-btn reset-base"
              @click="irPara(3)"
            >
              {{ t(`${G}.IR_COLAR`) }}
            </button>
          </div>

          <p class="cas__passo-sup">
            {{ t(`${G}.PASSO_DE`, { atual: passo + 1, total: PASSOS.length }) }}
          </p>

          <!-- Os quatro passos EMPILHADOS na mesma área (T17, 13/10/2026): a área
               fica com a altura do maior e o "Próximo" não muda de lugar —
               com v-if ela ia de 196 a 522px e o botão andava 163px. Só o
               atual aparece; os outros ficam invisíveis e inertes. -->
          <div class="cas__palco">
            <!-- 1. Abrir o Asaas -->
            <div v-bind="estadoDoPasso(0)" key="p0" class="cas__passo">
              <h3 class="cas__passo-tit">{{ t(`${G}.P1_TIT`) }}</h3>
              <p class="cas__texto">{{ t(`${G}.P1_DICA`) }}</p>
              <section class="cas__caixa cas__caixa--azul">
                <p class="cas__caixa-tit">
                  <i class="i-ph-key-fill" aria-hidden="true" />
                  {{ t(`${G}.O_QUE_E_TIT`) }}
                </p>
                <p class="cas__texto">{{ t(`${G}.O_QUE_E`) }}</p>
              </section>
              <button
                type="button"
                class="advup-btn-forte reset-base cas__grande"
                @click="abrirAsaas"
              >
                <i class="i-ph-arrow-square-out-bold" aria-hidden="true" />
                {{ t(`${G}.ABRIR_ASAAS`) }}
              </button>
              <p class="cas__nota">{{ t(`${G}.P1_NOTA`) }}</p>

              <div class="cas__ajuda">
                <details class="cas__pergunta">
                  <summary>{{ t(`${G}.FAQ_CUSTO`) }}</summary>
                  <ul class="cas__tarifas">
                    <li v-for="linha in tarifas" :key="linha.chave">
                      <span>{{ t(`${G}.TARIFA.${linha.chave}`) }}</span>
                      <strong>{{ linha.valor }}</strong>
                    </li>
                  </ul>
                  <p class="cas__nota">
                    {{
                      t(`${G}.TARIFA.PROMO`, {
                        valor: promo.valor,
                        meses: promo.meses,
                      })
                    }}
                    {{ t(`${G}.TARIFA.FONTE`, { data: CONFERIDO_EM }) }}
                    <a :href="FONTE" target="_blank" rel="noopener noreferrer">
                      {{ t(`${G}.TARIFA.VER_TABELA`) }}
                    </a>
                  </p>
                </details>
                <details class="cas__pergunta">
                  <summary>{{ t(`${G}.FAQ_SEGURO`) }}</summary>
                  <p class="cas__texto">{{ t(`${G}.FAQ_SEGURO_R`) }}</p>
                </details>
                <details class="cas__pergunta">
                  <summary>{{ t(`${G}.FAQ_SEM_CONTA`) }}</summary>
                  <p class="cas__texto">
                    {{ t(`${G}.FAQ_SEM_CONTA_R`) }}
                    <a
                      :href="SITE_DO_ASAAS"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ t(`${G}.CRIAR_CONTA`) }}
                    </a>
                  </p>
                </details>
              </div>
            </div>

            <!-- 2. Gerar a chave -->
            <div v-bind="estadoDoPasso(1)" key="p1" class="cas__passo">
              <h3 class="cas__passo-tit">{{ t(`${G}.P2_TIT`) }}</h3>
              <p class="cas__texto">{{ t(`${G}.P2_DICA`) }}</p>
              <button
                type="button"
                class="cas__foto reset-base"
                :class="{ 'cas__foto--ampliada': fotoAmpliada }"
                :aria-label="
                  fotoAmpliada ? t(`${G}.FOTO_REDUZIR`) : t(`${G}.FOTO_AMPLIAR`)
                "
                @click="fotoAmpliada = !fotoAmpliada"
              >
                <img :src="FOTO" :alt="t(`${G}.FOTO_ALT`)" />
                <span class="cas__foto-dica" aria-hidden="true">
                  <i
                    :class="
                      fotoAmpliada
                        ? 'i-hugeicons-zoom-out-area'
                        : 'i-hugeicons-zoom-in-area'
                    "
                  />
                </span>
              </button>
              <ul class="cas__lista cas__lista--miuda">
                <li class="cas__linha">
                  <i
                    class="i-ph-cursor-click-fill cas__ico-marca"
                    aria-hidden="true"
                  />
                  {{ t(`${G}.P2_ITEM_1`) }}
                </li>
                <li class="cas__linha">
                  <i class="i-ph-tag-fill cas__ico-marca" aria-hidden="true" />
                  {{ t(`${G}.P2_ITEM_2`) }}
                </li>
                <li class="cas__linha">
                  <i
                    class="i-ph-infinity-bold cas__ico-marca"
                    aria-hidden="true"
                  />
                  {{ t(`${G}.P2_ITEM_3`) }}
                </li>
              </ul>
            </div>

            <!-- 3. Copiar -->
            <div v-bind="estadoDoPasso(2)" key="p2" class="cas__passo">
              <h3 class="cas__passo-tit">{{ t(`${G}.P3_TIT`) }}</h3>
              <p class="cas__texto">{{ t(`${G}.P3_DICA`) }}</p>
              <div class="cas__amostra" aria-hidden="true">
                <span class="cas__amostra-chave">{{ t(`${G}.AMOSTRA`) }}</span>
                <span class="cas__amostra-copiar">
                  <i class="i-ph-copy-bold" />
                </span>
              </div>
              <section class="cas__caixa cas__caixa--ambar">
                <p class="cas__caixa-tit">
                  <i class="i-ph-warning-fill" aria-hidden="true" />
                  {{ t(`${G}.P3_ATENCAO_TIT`) }}
                </p>
                <p class="cas__texto">{{ t(`${G}.P3_ATENCAO`) }}</p>
              </section>
            </div>

            <!-- 4. Colar e conectar -->
            <form
              v-bind="estadoDoPasso(3)"
              key="p3"
              class="cas__passo"
              @submit.prevent="conectar"
            >
              <label for="cas-chave" class="cas__passo-tit">
                {{ t(`${G}.P4_TIT`) }}
              </label>
              <p class="cas__texto">{{ t(`${G}.P4_DICA`) }}</p>
              <div class="cas__campo">
                <input
                  id="cas-chave"
                  ref="campo"
                  v-model="chave"
                  :type="mostrar ? 'text' : 'password'"
                  autocomplete="off"
                  spellcheck="false"
                  class="advup-campo reset-base cas__chave"
                  :placeholder="t(`${G}.CAMPO`)"
                  :disabled="conectando"
                />
                <button
                  type="button"
                  class="advup-btn advup-btn--ico reset-base"
                  :aria-label="mostrar ? t(`${G}.ESCONDER`) : t(`${G}.MOSTRAR`)"
                  @click="mostrar = !mostrar"
                >
                  <i
                    :class="
                      mostrar ? 'i-hugeicons-view-off' : 'i-hugeicons-view'
                    "
                    aria-hidden="true"
                  />
                </button>
                <button
                  v-if="podeColar"
                  type="button"
                  class="advup-btn reset-base"
                  :disabled="conectando"
                  @click="colar"
                >
                  <i class="i-hugeicons-clipboard" aria-hidden="true" />
                  {{ t(`${G}.COLAR`) }}
                </button>
              </div>
              <p
                v-if="leitura && !erro"
                class="cas__leitura"
                :class="`cas__leitura--${leitura.tom}`"
              >
                <i
                  :class="
                    leitura.tom === 'rubi'
                      ? 'i-ph-warning-circle-fill'
                      : 'i-ph-check-circle-fill'
                  "
                  aria-hidden="true"
                />
                {{ t(`${G}.${leitura.chave}`) }}
              </p>

              <ul v-if="conectando" class="cas__lista cas__lista--miuda">
                <li v-for="etapa in etapas" :key="etapa" class="cas__linha">
                  <i
                    class="i-ph-circle-notch advup-gira cas__ico-marca"
                    aria-hidden="true"
                  />
                  {{ etapa }}
                </li>
              </ul>
              <section v-else class="cas__caixa cas__caixa--azul">
                <p class="cas__caixa-tit">{{ t(`${G}.FAZ_TIT`) }}</p>
                <ul class="cas__lista cas__lista--miuda">
                  <li
                    v-for="item in oQueConectarFaz"
                    :key="item.icone"
                    class="cas__linha"
                  >
                    <i
                      class="cas__ico-marca"
                      :class="[item.icone]"
                      aria-hidden="true"
                    />
                    {{ item.texto }}
                  </li>
                </ul>
              </section>
              <p v-if="erro" class="advup-modal__erro" role="alert">
                {{ erro }}
              </p>
            </form>
          </div>
        </div>

        <div class="advup-modal__rodape advup-modal__rodape--acoes">
          <template v-if="resultado">
            <button
              type="button"
              class="advup-btn-forte reset-base"
              @click="emit('fechar')"
            >
              {{ t(`${G}.PRONTO`) }}
            </button>
          </template>
          <template v-else>
            <button
              v-if="passo === 0"
              type="button"
              class="advup-btn advup-btn--vazado reset-base"
              @click="emit('fechar')"
            >
              {{ t(`${G}.CANCELAR`) }}
            </button>
            <button
              v-else
              type="button"
              class="advup-btn advup-btn--vazado reset-base"
              :disabled="conectando"
              @click="voltar"
            >
              <i class="i-ph-arrow-left-bold" aria-hidden="true" />
              {{ t(`${G}.VOLTAR`) }}
            </button>
            <button
              v-if="passo < PASSOS.length - 1"
              type="button"
              class="advup-btn-forte reset-base"
              @click="proximo"
            >
              {{ t(`${G}.PROXIMO`) }}
              <i class="i-ph-arrow-right-bold" aria-hidden="true" />
            </button>
            <button
              v-else
              type="button"
              class="advup-btn-forte reset-base"
              :disabled="!podeConectar"
              @click="conectar"
            >
              <i
                :class="
                  conectando
                    ? 'i-ph-circle-notch advup-gira'
                    : 'i-ph-plugs-connected'
                "
                aria-hidden="true"
              />
              {{ conectando ? t(`${G}.CONECTANDO`) : t(`${G}.CONECTAR`) }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cas {
  --modal-larg: 40rem;
}

.cas__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

/* Os passos na mesma célula da grade: a área tem a altura do maior. */
.cas__palco {
  display: grid;
}

.cas__passo {
  display: flex;
  grid-area: 1 / 1;
  flex-direction: column;
  gap: var(--esp-3);
}

.cas__passo--oculto {
  visibility: hidden;
}

/* Cada passo chega deslizando de leve — é a resposta ao "Próximo". A classe
   entra quando o passo vira o atual, e a animação começa de novo. */
.cas__passo--atual {
  animation: advupCapaSobe var(--mov-3) var(--mov-curva) both;
}

.cas__passo-sup {
  margin: 0;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--blue-11));
}

.cas__passo-tit {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--txt-num);
  font-weight: var(--peso-titulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-titulo);
  color: rgb(var(--slate-12));
}

.cas__texto {
  margin: 0;
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.cas__nota {
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.cas__nota > a,
.cas__texto > a {
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  text-decoration: underline;
}

.cas__grande {
  align-self: flex-start;
  min-height: 44px;
  padding-inline: var(--esp-5);
}

/* Caixa de explicação: o tom diz se é informação (azul) ou atenção (âmbar). */
.cas__caixa {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  padding: var(--esp-3) var(--esp-4);
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-4));
}

.cas__caixa--azul {
  background: rgb(var(--blue-2));
  box-shadow: inset 0 0 0 1px rgb(var(--blue-5));
}

.cas__caixa--ambar {
  background: rgb(var(--amber-2));
  box-shadow: inset 0 0 0 1px rgb(var(--amber-5));
}

.cas__caixa-tit {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.cas__caixa--azul .cas__caixa-tit > i {
  color: rgb(var(--blue-10));
}

.cas__caixa--ambar .cas__caixa-tit > i {
  color: rgb(var(--amber-10));
}

/* Perguntas frequentes: abrem no lugar, sem tirar a pessoa do passo. */
.cas__ajuda {
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgb(var(--slate-4));
}

.cas__pergunta {
  border-bottom: 1px solid rgb(var(--slate-4));
}

.cas__pergunta > summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
  list-style: none;
  cursor: pointer;
}

.cas__pergunta > summary::-webkit-details-marker {
  display: none;
}

.cas__pergunta > summary::after {
  content: '+';
  font-size: var(--txt-bloco);
  color: rgb(var(--slate-10));
  transition: transform var(--mov-2) var(--mov-curva);
}

.cas__pergunta[open] > summary::after {
  transform: rotate(45deg);
}

.cas__pergunta > :not(summary) {
  margin-bottom: var(--esp-3);
}

.cas__tarifas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0 0 var(--esp-2);
  padding: 0;
  list-style: none;
}

.cas__tarifas > li {
  display: flex;
  justify-content: space-between;
  gap: var(--esp-3);
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}

.cas__tarifas strong {
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}

/* A foto do Asaas: um toque aproxima o botão que importa. */
.cas__foto {
  position: relative;
  display: block;
  overflow: hidden;
  width: 100%;
  margin: 0;
  padding: 0;
  border-radius: var(--r-3);
  background: none;
  box-shadow:
    0 0 0 1px rgb(var(--slate-4)),
    0 6px 18px -10px rgb(15 23 42 / 0.35);
  cursor: zoom-in;
}

.cas__foto > img {
  display: block;
  width: 100%;
  height: auto;
  transition: transform var(--mov-3) var(--mov-curva);
}

/* Ampliada: aproxima a área do botão "Gerar chave de API" (canto direito). */
.cas__foto--ampliada {
  cursor: zoom-out;
}

.cas__foto--ampliada > img {
  transform: scale(1.9);
  transform-origin: 92% 50%;
}

.cas__foto:focus-visible {
  outline: 2px solid rgb(var(--blue-8));
  outline-offset: 2px;
}

.cas__foto-dica {
  position: absolute;
  inset-block-end: var(--esp-2);
  inset-inline-end: var(--esp-2);
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--r-redondo);
  background: rgb(15 23 42 / 0.7);
  color: #fff;
}

.cas__foto-dica > i {
  width: var(--ico-2);
  height: var(--ico-2);
  font-size: var(--ico-2);
}

.cas__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

.cas__lista--miuda {
  gap: var(--esp-2);
}

.cas__linha {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-12));
}

.cas__linha > i {
  flex-shrink: 0;
  width: var(--ico-3);
  height: var(--ico-3);
  font-size: var(--ico-3);
}

.cas__linha--teal > i {
  color: rgb(var(--teal-10));
}

.cas__linha--ambar > i {
  color: rgb(var(--amber-10));
}

.cas__linha > .cas__ico-marca {
  color: rgb(var(--blue-10));
}

/* A chave de mentira do passo 3: mostra o formato sem mostrar segredo. */
.cas__amostra {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3);
  padding: var(--esp-3) var(--esp-4);
  border-radius: var(--r-3);
  background: rgb(var(--slate-2));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-5));
}

.cas__amostra-chave {
  overflow: hidden;
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cas__amostra-copiar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--r-3);
  background: rgb(var(--blue-9));
  color: #fff;
  box-shadow: 0 4px 12px -4px rgb(var(--blue-9) / 0.7);
}

.cas__amostra-copiar > i {
  width: var(--ico-2);
  height: var(--ico-2);
  font-size: var(--ico-2);
}

.cas__campo {
  display: flex;
  gap: var(--esp-2);
}

.cas__chave {
  flex: 1;
  min-width: 0;
}

/* O que a chave colada parece ser — antes de ir ao servidor. */
.cas__leitura {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
}

.cas__leitura > i {
  width: var(--ico-2);
  height: var(--ico-2);
  font-size: var(--ico-2);
}

.cas__leitura--teal {
  color: rgb(var(--teal-11));
}

.cas__leitura--ambar {
  color: rgb(var(--amber-11));
}

.cas__leitura--rubi {
  color: rgb(var(--ruby-11));
}

/* Voltou da aba do Asaas. */
.cas__voltou {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-2) var(--esp-2) var(--esp-2) var(--esp-4);
  border-radius: var(--r-4);
  background: rgb(var(--blue-3));
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--blue-12));
  animation: advupCapaSobe var(--mov-3) var(--mov-curva) both;
}

.cas__voltou > i {
  flex-shrink: 0;
  width: var(--ico-3);
  height: var(--ico-3);
  font-size: var(--ico-3);
  color: rgb(var(--blue-10));
}

.cas__voltou > span {
  flex: 1;
}

@media (max-width: 639px) {
  .cas__campo {
    flex-wrap: wrap;
  }

  .cas__chave {
    flex-basis: 100%;
  }

  .cas__voltou {
    flex-wrap: wrap;
  }

  .cas__grande {
    align-self: stretch;
    justify-content: center;
  }
}
</style>
